"use client";

import { Bot, Ellipsis, Link2, Pencil, Pin, PinOff, Trash2, Workflow } from "lucide-react";
import { memo, type Ref } from "react";

import { displayChatTitle, type ChatHistoryItem } from "@/features/chat/multi/history/chat-history";
import { cn } from "@/shared/lib/utils/cn";
import { formatRelativeTime } from "@/shared/lib/utils/format-relative-time";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/ui/shadcn-components/dropdown-menu";
import { ChatRowStatusDot, chatRowStatusCaption } from "./ChatRowStatusDot";
import { useChatRowFlags } from "./useChatRowFlags";

type SidebarChatRowProps = {
  chat: ChatHistoryItem;
  isActive: boolean;
  activeChatRef?: Ref<HTMLButtonElement>;
  onOpenChat: (id: string) => void;
  onOpenWorkflowCanvas?: (id: string) => void;
  onPinChat: (chat: ChatHistoryItem) => void;
  onRenameChat: (chat: ChatHistoryItem) => void;
  onShareChat: (chat: ChatHistoryItem) => void;
  onDeleteChat: (chat: ChatHistoryItem) => void;
};

export const SidebarChatRow = memo(function SidebarChatRow({
  chat,
  isActive,
  activeChatRef,
  onOpenChat,
  onOpenWorkflowCanvas,
  onPinChat,
  onRenameChat,
  onShareChat,
  onDeleteChat,
}: SidebarChatRowProps) {
  const title = displayChatTitle(chat);
  const { status } = useChatRowFlags(chat.id);
  const live = status === "building" || status === "working";
  const needsInput = status === "needs_input";

  return (
    <div
      className={cn(
        "group flex w-full min-w-0 items-center overflow-hidden rounded-xl border text-sidebar-foreground transition-colors",
        isActive
          ? "border-border/40 bg-card shadow-sm"
          : "border-transparent hover:border-border/40 hover:bg-muted/50 hover:shadow-sm",
      )}
    >
      <button
        ref={isActive ? activeChatRef : null}
        type="button"
        title={title}
        aria-current={isActive ? "page" : undefined}
        onClick={() => onOpenChat(chat.id)}
        className="min-w-0 flex-1 overflow-hidden py-2 pl-3 text-left text-sm"
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <div
            className={cn(
              "shrink-0 rounded-lg p-1.5 transition-colors",
              isActive
                ? "bg-accent text-primary"
                : "bg-muted/60 text-muted-foreground group-hover:bg-accent group-hover:text-primary",
            )}
          >
            <Bot className="size-3.5" />
          </div>
          <div className="min-w-0 flex-1 overflow-hidden">
            <p className="block truncate text-[13px] leading-tight font-medium text-foreground">
              {title}
            </p>
            <p
              className={cn(
                "block truncate text-[11px] leading-tight font-medium",
                live && "text-primary",
                needsInput && "text-amber-600 dark:text-amber-500",
                !live && !needsInput && "text-muted-foreground/50",
              )}
            >
              {chatRowStatusCaption(status, formatRelativeTime(chat.updatedAt))}
            </p>
          </div>
          {chat.pinned ? (
            <Pin className="size-3 shrink-0 text-muted-foreground/60" aria-label="Pinned" />
          ) : null}
          <ChatRowStatusDot status={status} />
        </div>
      </button>
      {status === "building" && onOpenWorkflowCanvas ? (
        <button
          type="button"
          title="Open workflow canvas"
          aria-label={`Open workflow for ${title}`}
          onClick={() => onOpenWorkflowCanvas(chat.id)}
          className="mr-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg text-primary transition-colors hover:bg-accent"
        >
          <Workflow className="size-3.5 animate-pulse" />
        </button>
      ) : null}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={`Actions for ${title}`}
            className="mr-1 flex size-7 shrink-0 items-center justify-center rounded-lg text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus:opacity-100 group-hover:opacity-100 data-[state=open]:bg-muted data-[state=open]:opacity-100"
          >
            <Ellipsis className="size-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side="right" className="w-40">
          <DropdownMenuItem onSelect={() => void onShareChat(chat)}>
            <Link2 />
            Share
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => onRenameChat(chat)}>
            <Pencil />
            Rename
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => onPinChat(chat)}>
            {chat.pinned ? <PinOff /> : <Pin />}
            {chat.pinned ? "Unpin chat" : "Pin chat"}
          </DropdownMenuItem>
          <DropdownMenuItem variant="destructive" onSelect={() => onDeleteChat(chat)}>
            <Trash2 />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
});
